import {
  Icon,
  InlineStack,
  Link,
  Paragraph,
  PlainButton,
  Popover,
  PopoverContent,
  Stack,
} from '@customerio/pluma-components/react';
import { getDesignStudioTypeLabel } from '../utils/item';
import type { UsageRecord } from '../item/usage-links';

/**
 * One thing that uses the item, inside its surface's accordion.
 *
 * A segment and a campaign carry a description worth reading beside the
 * name — it is usually what says whether this is the one in the way.
 */

type UsageItemProps = {
  record: UsageRecord;
  /** Where the record opens, or nothing when it carries no id to open. */
  href: string | null;
};

/** What a row says under its name, where it has something to say. */
function descriptionOf(record: UsageRecord): string | null {
  if ('segment_id' in record || 'campaign_id' in record) {
    return record.description === '' ? null : record.description;
  }
  if ('short_id' in record) {
    return getDesignStudioTypeLabel(record.type);
  }
  return null;
}

export function UsageItem({ record, href }: UsageItemProps) {
  const description = descriptionOf(record);
  const isArchivedSegment = 'segment_id' in record && record.archived;

  return (
    <Stack gap="100">
      <InlineStack alignBlock="center" gap="100">
        {isArchivedSegment ? (
          <Popover shouldTriggerOnHover shouldTriggerOnFocus shouldTriggerOnClick={false}>
            {(context) => (
              <>
                {/* A button, not a span: this is the only thing that says the
                    segment is archived, so it has to be reachable without a
                    mouse and it has to have a name. */}
                <PlainButton
                  aria-label="Usage detail"
                  ref={context.reference.setReference}
                  {...context.reference.getReferenceProps()}
                >
                  <Icon name="info" size="sm" color="subtle" display="block" />
                </PlainButton>
                <PopoverContent>This segment is archived, and still counts as usage.</PopoverContent>
              </>
            )}
          </Popover>
        ) : null}
        {href == null ? (
          <Paragraph>{record.name}</Paragraph>
        ) : (
          <Link href={href} icon="exit" iconPosition="trailing" variant="primary" isUnderlined={false} target="_blank">
            {record.name}
          </Link>
        )}
      </InlineStack>
      {description == null || description === '' ? null : <Paragraph variant="secondary">{description}</Paragraph>}
    </Stack>
  );
}
